import { useState } from "react";
import { useKundenakte, useKundenportalErstellen, useKundenportalWiderrufen, datum } from "../../hooks/useVertraege";
import { fehlertext } from "../../lib/fehler";

/**
 * Kundenakte — ein dauerhafter Lesezugang für den Kunden.
 *
 * Bisher bekam er einen Token je Vertrag: einmal zum Zustimmen, danach ein
 * Blick auf genau dieses Dokument. Wer ein Jahr später seinen
 * Leistungsnachweis sucht, findet die alte Mail nicht mehr und ruft an. Der
 * Link hier bleibt gültig und zeigt alles, was zu diesem Kunden gehört.
 *
 * Er ist nicht passwortgeschützt — deshalb steht der Widerruf gleich daneben.
 */
export default function Kundenzugang({ kundeId }: { kundeId: string }) {
  const { data: akte } = useKundenakte(kundeId);
  const erstellen = useKundenportalErstellen(kundeId);
  const widerrufen = useKundenportalWiderrufen(kundeId);
  const [kopiert, setKopiert] = useState(false);
  const [fehler, setFehler] = useState("");
  const portal = akte?.portal ?? null;

  async function lauf(fn: () => Promise<unknown>) {
    setFehler("");
    try { await fn(); }
    catch (e) { setFehler(fehlertext(e) || "Fehlgeschlagen."); }
  }

  return (
    <div className="pf-card" style={{ marginBottom: 16 }}>
      <div style={{ fontWeight: 700 }}>Kundenakte</div>
      <div style={{ fontSize: 12.5, color: "var(--pf-ink-3)", marginTop: 4, lineHeight: 1.55 }}>
        Ein dauerhafter Link auf alle Verträge, Einsätze und Leistungsnachweise dieses
        Kunden. Reine Lesesicht — ändern kann er dort nichts.
        {akte && akte.vertraege.length > 1
          ? ` Er umfasst ${akte.vertraege.length} Verträge.`
          : ""}
      </div>

      {portal ? (
        <>
          <div style={{
            marginTop: 10, padding: "9px 11px", borderRadius: 8, fontSize: 12.5,
            background: "var(--pf-bg-2, #f6f7f3)", wordBreak: "break-all",
          }}>
            {portal.link}
          </div>
          <div style={{ fontSize: 12.5, color: "var(--pf-ink-3)", marginTop: 6 }}>
            seit {datum(portal.erstellt_am)} ·{" "}
            {portal.zugriffe === 0
              ? "noch nicht geöffnet"
              : `${portal.zugriffe}× geöffnet, zuletzt ${datum(portal.letzter_zugriff)}`}
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
            <button className="pf-btn is-ghost is-small" onClick={() => {
              navigator.clipboard?.writeText(portal.link);
              setKopiert(true);
            }}>{kopiert ? "✓ Kopiert" : "Link kopieren"}</button>
            <a className="pf-btn is-ghost is-small" href={portal.link}
              target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
              Öffnen
            </a>
            <button className="pf-btn is-ghost is-small" disabled={erstellen.isPending}
              onClick={() => lauf(async () => { await erstellen.mutateAsync(); setKopiert(false); })}>
              Neuen Link geben
            </button>
            <button className="pf-btn is-ghost is-small" style={{ color: "var(--pf-red)" }}
              disabled={widerrufen.isPending}
              onClick={() => lauf(() => widerrufen.mutateAsync())}>
              Zugang zurückziehen
            </button>
          </div>
          <div style={{ fontSize: 12, color: "var(--pf-ink-3)", marginTop: 8, lineHeight: 1.5 }}>
            „Neuen Link geben" macht den alten sofort ungültig — das ist die Notbremse,
            wenn ein Kunde meldet, sein Link sei in falsche Hände geraten.
          </div>
        </>
      ) : (
        <button className="pf-btn is-ghost" style={{ marginTop: 10 }} disabled={erstellen.isPending}
          onClick={() => lauf(() => erstellen.mutateAsync())}>
          {erstellen.isPending ? "Erzeuge…" : "Zugang erzeugen"}
        </button>
      )}

      {fehler && <div style={{ marginTop: 8, fontSize: 13, color: "var(--pf-red)" }}>{fehler}</div>}
    </div>
  );
}

